import React from 'react';

interface PaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  isLoading?: boolean;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [25, 50, 100],
  isLoading = false
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const clampedPage = Math.min(Math.max(page, 1), totalPages);
  const startIndex = totalCount === 0 ? 0 : (clampedPage - 1) * pageSize + 1;
  const endIndex = Math.min(clampedPage * pageSize, totalCount);

  return (
    <div className="border-t border-slate-100 pt-4 mt-4 space-y-2">
      <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
        <div className="text-xs text-slate-500">
          Showing <span className="font-semibold text-slate-700">{startIndex}</span> to{' '}
          <span className="font-semibold text-slate-700">{endIndex}</span> of{' '}
          <span className="font-semibold text-slate-700">{totalCount}</span>
        </div>
        {onPageSizeChange && (
          <select
            aria-label="Items per page"
            className="h-8 min-w-[100px] justify-self-start sm:justify-self-end rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 shadow-sm outline-none transition focus:border-teal-400 focus:ring-2 focus:ring-teal-100 disabled:cursor-not-allowed disabled:opacity-50"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={isLoading}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size} / page
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex items-center gap-2 sm:justify-end">
        <button
          onClick={() => onPageChange(Math.max(1, clampedPage - 1))}
          disabled={isLoading || clampedPage <= 1}
          className="h-8 px-3 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>
        <span className="min-w-[105px] text-center text-xs font-semibold text-slate-600">
          Page {clampedPage} of {totalPages}
        </span>
        <button
          onClick={() => onPageChange(Math.min(totalPages, clampedPage + 1))}
          disabled={isLoading || clampedPage >= totalPages}
          className="h-8 px-3 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
};
